import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, Trash2, RefreshCw, Search, Copy, Check, X, Pencil, Image as ImageIcon,
  Film, Tag as TagIcon, FolderInput, AlertTriangle, Eye, EyeOff,
} from 'lucide-react';
import { api, API_BASE } from '../../lib/api';

/**
 * MODARIA · Media Library.
 *
 *   • Upload múltiple (drag & drop o explorador).
 *   • Filtros: kind, tag, section_hint, búsqueda.
 *   • Grid con thumb · dimensiones · tamaño.
 *   • Copiar URL al portapapeles (1-click).
 *   • Replace (reemplaza bytes manteniendo URL + referencias).
 *   • Assign a sección (showcase.gallery, looks_hall_of_fame.items…).
 *   • Delete.
 */

// Presets de secciones donde el admin puede anclar un asset.  La
// primera entrada de cada grupo es el nombre canónico que consultan
// los componentes públicos.
const SECTION_PRESETS = [
  { key: 'showcase.gallery',          label: 'Showcase · Galería' },
  { key: 'intro_hero.carousel',       label: 'Hero · Carousel' },
  { key: 'looks_hall_of_fame.items',  label: 'Looks · Hall of Fame' },
  { key: 'manifesto.visual',          label: 'Manifiesto · Visual' },
  { key: 'features.visual',           label: 'Features · Visual' },
  { key: 'how.media',                 label: 'Pipeline · Media' },
  { key: 'reel.clips',                label: 'Reel · Clips' },
];

const TAG_PRESETS = [
  'fashion-sketch', 'editorial', 'modaria-aesthetic', 'hero',
  'product', 'studio', 'before', 'after',
];

function formatSize(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('');  // '' | 'true' | 'false'
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const inputRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = {};
      if (kindFilter) params.kind = kindFilter;
      if (tagFilter) params.tag = tagFilter;
      if (sectionFilter) params.section = sectionFilter;
      if (enabledFilter) params.enabled = enabledFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get('/admin/media', { params });
      setItems(data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [kindFilter, tagFilter, sectionFilter, enabledFilter, search]);

  useEffect(() => { reload(); }, [reload]);

  async function handleUpload(files, presetSection = '', presetTags = []) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setErr('');
    try {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      fd.append('tags', JSON.stringify(presetTags));
      fd.append('section_hints', JSON.stringify(presetSection ? [presetSection] : []));
      await api.post('/admin/media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(asset) {
    if (!window.confirm(`¿Eliminar "${asset.original_name}"? Esta acción no se puede deshacer.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/media/${asset.id}`);
      await reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleEnabled(asset) {
    // Optimistic update for instant feedback, revert on error.
    const nextEnabled = !asset.enabled;
    setItems((prev) => prev.map((it) => it.id === asset.id ? { ...it, enabled: nextEnabled } : it));
    try {
      const fd = new FormData();
      fd.append('enabled', nextEnabled ? 'true' : 'false');
      await api.patch(`/admin/media/${asset.id}/enabled`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (e) {
      // Rollback.
      setItems((prev) => prev.map((it) => it.id === asset.id ? { ...it, enabled: asset.enabled } : it));
      setErr(e.response?.data?.detail || e.message);
    }
  }

  async function handleSaveMeta(asset, patch) {
    setBusy(true);
    try {
      const fd = new FormData();
      if (patch.tags !== undefined) fd.append('tags', JSON.stringify(patch.tags));
      if (patch.section_hints !== undefined) fd.append('section_hints', JSON.stringify(patch.section_hints));
      if (patch.alt_text_es !== undefined) fd.append('alt_text_es', patch.alt_text_es);
      if (patch.alt_text_en !== undefined) fd.append('alt_text_en', patch.alt_text_en);
      if (patch.file) fd.append('file', patch.file);
      await api.put(`/admin/media/${asset.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditingId(null);
      await reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function copyUrl(asset) {
    const full = `${API_BASE}${asset.url}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopiedId(asset.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (_) { /* noop */ }
  }

  const totalSize = useMemo(
    () => items.reduce((s, it) => s + (it.size_bytes || 0), 0),
    [items],
  );

  return (
    <div className="space-y-8" data-testid="media-library-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-eyebrow text-acid mb-2">Biblioteca · Imágenes + Vídeos</div>
          <h1 className="text-4xl font-display text-ink mb-3">Media Library</h1>
          <p className="text-sm text-ink-muted max-w-2xl">
            Subida centralizada de imágenes y vídeos (hasta 100 MB cada uno).
            Asigna tags y secciones · los componentes públicos tiran del servicio
            <span className="text-acid font-mono"> /api/media/by-section/&lt;key&gt;</span>.
          </p>
        </div>
        <button
          onClick={reload}
          disabled={busy}
          className="flex items-center gap-2 px-4 py-2 bg-bg-surface border border-line hover:border-acid text-sm font-mono text-ink-muted hover:text-acid transition-colors disabled:opacity-50"
          data-testid="media-refresh-btn"
        >
          <RefreshCw size={14} /> Recargar
        </button>
      </div>

      {err && (
        <div className="bg-danger/10 border border-danger/40 text-danger px-4 py-3 flex items-center gap-2 text-sm" data-testid="media-error-banner">
          <AlertTriangle size={16} /> {err}
        </div>
      )}

      {/* ================================================================
       * Uploader
       * ============================================================== */}
      <UploadDropzone busy={busy} onUpload={(files) => handleUpload(files)} inputRef={inputRef} />

      {/* ================================================================
       * Filters
       * ============================================================== */}
      <div className="flex flex-wrap items-center gap-3 bg-bg-surface border border-line p-4" data-testid="media-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="text-ink-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre…"
            className="flex-1 bg-transparent text-sm text-ink placeholder-ink-dim focus:outline-none"
            data-testid="media-search-input"
          />
        </div>
        <SelectChip
          value={kindFilter}
          onChange={setKindFilter}
          options={[['', 'Todo'], ['image', 'Imagen'], ['video', 'Vídeo']]}
          testId="media-kind-filter"
          icon={<ImageIcon size={12} />}
        />
        <SelectChip
          value={tagFilter}
          onChange={setTagFilter}
          options={[['', 'Todos los tags'], ...TAG_PRESETS.map((t) => [t, t])]}
          testId="media-tag-filter"
          icon={<TagIcon size={12} />}
        />
        <SelectChip
          value={sectionFilter}
          onChange={setSectionFilter}
          options={[['', 'Toda sección'], ...SECTION_PRESETS.map((p) => [p.key, p.label])]}
          testId="media-section-filter"
          icon={<FolderInput size={12} />}
        />
        <SelectChip
          value={enabledFilter}
          onChange={setEnabledFilter}
          options={[['', 'Todos'], ['true', 'Sólo activos'], ['false', 'Sólo desactivados']]}
          testId="media-enabled-filter"
          icon={<Eye size={12} />}
        />
        <span className="text-xs font-mono text-ink-muted ml-auto">
          {items.length} {items.length === 1 ? 'archivo' : 'archivos'} · {formatSize(totalSize)}
        </span>
      </div>

      {/* ================================================================
       * Grid
       * ============================================================== */}
      {loading ? (
        <div className="text-ink-muted text-sm">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-ink-muted text-sm">
          Sin medios. Sube archivos arriba o ajusta los filtros.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
          {items.map((asset) => (
            <MediaCard
              key={asset.id}
              asset={asset}
              copied={copiedId === asset.id}
              expanded={editingId === asset.id}
              onCopy={() => copyUrl(asset)}
              onDelete={() => handleDelete(asset)}
              onToggleEnabled={() => handleToggleEnabled(asset)}
              onToggleEdit={() => setEditingId(editingId === asset.id ? null : asset.id)}
              onSave={(patch) => handleSaveMeta(asset, patch)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


/* ====================================================================== */
/* Dropzone upload                                                        */
/* ====================================================================== */
function UploadDropzone({ busy, onUpload, inputRef }) {
  const [dragOver, setDragOver] = useState(false);
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    onUpload(Array.from(e.dataTransfer.files || []));
  }
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={`border border-dashed p-8 text-center cursor-pointer transition-colors ${
        dragOver ? 'border-acid bg-acid/5' : 'border-line hover:border-acid/50'
      }`}
      data-testid="media-dropzone"
    >
      <Upload size={22} className="mx-auto mb-2 text-acid" />
      <div className="text-sm text-ink font-mono mb-1">
        Arrastra aquí imágenes o vídeos, o haz click para seleccionar
      </div>
      <div className="text-xs text-ink-muted">
        PNG · JPG · WebP · GIF · SVG · MP4 · WebM · MOV · hasta 100 MB · multi-archivo
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        disabled={busy}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onUpload(files);
          e.target.value = '';
        }}
        data-testid="media-upload-input"
      />
    </div>
  );
}


/* ====================================================================== */
/* SelectChip                                                             */
/* ====================================================================== */
function SelectChip({ value, onChange, options, testId, icon }) {
  return (
    <div className="relative flex items-center gap-2 px-3 py-1.5 bg-bg border border-line">
      {icon && <span className="text-ink-muted">{icon}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-xs font-mono text-ink focus:outline-none pr-6"
        data-testid={testId}
      >
        {options.map(([k, label]) => (
          <option key={k} value={k}>{label}</option>
        ))}
      </select>
    </div>
  );
}


/* ====================================================================== */
/* Media card                                                             */
/* ====================================================================== */
function MediaCard({ asset, copied, expanded, onCopy, onDelete, onToggleEnabled, onToggleEdit, onSave }) {
  const thumb = asset.thumb_url ? `${API_BASE}${asset.thumb_url}` : null;
  const isVideo = asset.kind === 'video';
  const disabled = asset.enabled === false;
  return (
    <div
      className={`bg-bg-surface border overflow-hidden transition-opacity ${
        disabled ? 'border-line/60 opacity-60 hover:opacity-90' : 'border-line'
      }`}
      data-testid={`media-card-${asset.id}`}
    >
      <div className="relative aspect-square bg-bg">
        {thumb ? (
          <img
            src={thumb}
            alt={asset.alt_text_es || asset.original_name}
            className={`w-full h-full object-cover transition-all ${
              disabled ? 'grayscale' : ''
            }`}
            data-testid={`media-thumb-${asset.id}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-ink-muted">
            {isVideo ? <Film size={36} /> : <ImageIcon size={36} />}
          </div>
        )}
        {isVideo && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-mono bg-bg/80 border border-line text-acid flex items-center gap-1">
            <Film size={10} /> VIDEO
          </span>
        )}
        {disabled && (
          <span
            className="absolute bottom-2 left-2 px-2 py-0.5 text-[10px] font-mono bg-danger/80 text-bg flex items-center gap-1"
            data-testid={`media-disabled-badge-${asset.id}`}
          >
            <EyeOff size={10} /> OCULTO
          </span>
        )}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          <button
            onClick={onToggleEnabled}
            title={disabled ? 'Activar (mostrar en la landing)' : 'Desactivar (ocultar en la landing)'}
            className={`w-7 h-7 flex items-center justify-center bg-bg/80 border text-ink-muted transition-colors ${
              disabled ? 'border-danger/60 text-danger' : 'border-line hover:text-acid hover:border-acid'
            }`}
            data-testid={`media-toggle-${asset.id}`}
          >
            {disabled ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
          <button
            onClick={onCopy}
            title="Copiar URL"
            className="w-7 h-7 flex items-center justify-center bg-bg/80 border border-line text-ink-muted hover:text-acid hover:border-acid"
            data-testid={`media-copy-${asset.id}`}
          >
            {copied ? <Check size={12} className="text-acid" /> : <Copy size={12} />}
          </button>
          <button
            onClick={onToggleEdit}
            title="Editar metadata"
            className="w-7 h-7 flex items-center justify-center bg-bg/80 border border-line text-ink-muted hover:text-acid hover:border-acid"
            data-testid={`media-edit-${asset.id}`}
          >
            {expanded ? <X size={12} /> : <Pencil size={12} />}
          </button>
          <button
            onClick={onDelete}
            title="Eliminar"
            className="w-7 h-7 flex items-center justify-center bg-bg/80 border border-line text-ink-muted hover:text-danger hover:border-danger"
            data-testid={`media-delete-${asset.id}`}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="text-xs font-mono text-ink truncate" title={asset.original_name}>{asset.original_name}</div>
        <div className="flex items-center justify-between text-[10px] font-mono text-ink-muted">
          <span>{asset.width && asset.height ? `${asset.width}×${asset.height}` : '—'}</span>
          <span>{formatSize(asset.size_bytes)}</span>
        </div>
        {(asset.section_hints?.length > 0) && (
          <div className="flex flex-wrap gap-1" data-testid={`media-sections-${asset.id}`}>
            {asset.section_hints.map((s) => (
              <span key={s} className="px-1.5 py-0.5 text-[10px] font-mono border border-acid/40 text-acid bg-acid/10">
                {s.split('.').pop()}
              </span>
            ))}
          </div>
        )}
      </div>
      {expanded && <EditPanel asset={asset} onSave={onSave} onCancel={onToggleEdit} />}
    </div>
  );
}


/* ====================================================================== */
/* Edit panel                                                             */
/* ====================================================================== */
function EditPanel({ asset, onSave, onCancel }) {
  const [tags, setTags] = useState((asset.tags || []).join(', '));
  const [sections, setSections] = useState(new Set(asset.section_hints || []));
  const [altEs, setAltEs] = useState(asset.alt_text_es || '');
  const [altEn, setAltEn] = useState(asset.alt_text_en || '');
  const [replaceFile, setReplaceFile] = useState(null);
  const [saving, setSaving] = useState(false);

  function toggleSection(key) {
    setSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function save() {
    setSaving(true);
    await onSave({
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      section_hints: Array.from(sections),
      alt_text_es: altEs,
      alt_text_en: altEn,
      file: replaceFile || undefined,
    });
    setSaving(false);
  }

  return (
    <div className="p-3 border-t border-line space-y-3 bg-bg" data-testid={`media-edit-${asset.id}-panel`}>
      <div>
        <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted block mb-1">Tags (CSV)</label>
        <input
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder={TAG_PRESETS.slice(0, 3).join(', ')}
          className="w-full bg-bg-surface border border-line px-2 py-1 text-xs font-mono text-ink focus:border-acid focus:outline-none"
          data-testid={`media-edit-tags-${asset.id}`}
        />
      </div>
      <div>
        <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted block mb-1">Asignar a sección</label>
        <div className="flex flex-wrap gap-1">
          {SECTION_PRESETS.map((p) => {
            const on = sections.has(p.key);
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => toggleSection(p.key)}
                className={`px-2 py-1 text-[10px] font-mono border ${
                  on ? 'border-acid text-acid bg-acid/10' : 'border-line text-ink-muted hover:border-acid/50'
                }`}
                data-testid={`media-edit-section-${asset.id}-${p.key}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted block mb-1">Alt ES</label>
          <input
            value={altEs}
            onChange={(e) => setAltEs(e.target.value)}
            className="w-full bg-bg-surface border border-line px-2 py-1 text-xs text-ink focus:border-acid focus:outline-none"
            data-testid={`media-edit-alt-es-${asset.id}`}
          />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted block mb-1">Alt EN</label>
          <input
            value={altEn}
            onChange={(e) => setAltEn(e.target.value)}
            className="w-full bg-bg-surface border border-line px-2 py-1 text-xs text-ink focus:border-acid focus:outline-none"
            data-testid={`media-edit-alt-en-${asset.id}`}
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted block mb-1">
          Reemplazar archivo (opcional · mantiene la URL y referencias)
        </label>
        <input
          type="file"
          accept="image/*,video/*"
          onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
          className="w-full text-xs text-ink file:mr-2 file:py-1 file:px-2 file:bg-bg-surface file:text-ink-muted file:border file:border-line file:text-[10px]"
          data-testid={`media-edit-replace-${asset.id}`}
        />
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="flex-1 px-3 py-1.5 bg-acid hover:bg-acid-2 text-black text-xs font-mono uppercase tracking-wider disabled:opacity-40"
          data-testid={`media-edit-save-${asset.id}`}
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 bg-bg-surface border border-line text-ink-muted hover:text-acid text-xs font-mono uppercase tracking-wider"
          data-testid={`media-edit-cancel-${asset.id}`}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
